import React, { useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { motion } from 'framer-motion';
import { Check, Clock, Users, Award } from 'lucide-react';
export function TaekwondoPage() {
  useEffect(() => {
    document.title = "Taekwondo Classes | Walker's Martial Arts | Bloomington, Illinois";
  }, []);
  const ageGroups = [{
    name: 'Little Tigers',
    age: 'Ages 3-5',
    description: 'Our youngest students learn the basics of Taekwondo through fun, engaging activities that build coordination and confidence.',
    schedule: 'Monday-Thursday: 4:30 PM - 5:15 PM | Friday: 6:00 PM - 7:00 PM (Sparring)'
  }, {
    name: 'Kids & Teens',
    age: 'Ages 6-17',
    description: 'Students develop discipline, focus, and physical fitness while learning traditional Taekwondo techniques and forms.',
    schedule: 'Monday-Thursday: 6:00 PM - 8:00 PM | Friday: 6:00 PM - 7:00 PM (Sparring)'
  }, {
    name: 'Adults',
    age: 'Ages 18+',
    description: 'Adult classes focus on fitness, self-defense, and personal growth through the practice of Taekwondo.',
    schedule: 'Monday-Thursday: 6:00 PM - 8:00 PM | Friday: 6:00 PM - 7:00 PM (Sparring)'
  }];
  return <div className="bg-black min-h-screen" data-id="element-1026">
      <Navigation data-id="element-1027" />

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 overflow-hidden" data-id="element-1028">
        <div className="absolute inset-0 z-0" data-id="element-1029">
          <img src="https://images.squarespace-cdn.com/content/v1/6782c238cf9313348308674a/1736784232481-R4XWW960TB4VCKUGAOL8/TRANSPARENT+LEADERSHIP.png" alt="Taekwondo" className="w-full h-full object-cover opacity-20" data-id="element-1030" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" data-id="element-1031" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1032">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1033">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-1034">
              <span className="text-[#00D4FF]" data-id="element-1035">Taekwondo</span> Classes
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-1036">
              Traditional Korean martial art emphasizing kicks, discipline, and
              respect. Perfect for all ages and skill levels.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Age Groups */}
      <section className="py-20 bg-[#0a0a0a]" data-id="element-1037">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1038">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-1039">
            Classes by Age Group
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1040">
            {ageGroups.map((group, index) => <motion.div key={group.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-[#1a1a1a] p-8 rounded-2xl border border-gray-800" data-id="element-1041">
                <div className="flex items-center gap-3 mb-4" data-id="element-1042">
                  <Users className="w-6 h-6 text-[#00D4FF]" data-id="element-1043" />
                  <h3 className="text-2xl font-bold text-white" data-id="element-1044">
                    {group.name}
                  </h3>
                </div>
                <p className="text-[#00D4FF] font-semibold mb-4" data-id="element-1045">{group.age}</p>
                <p className="text-gray-400 mb-6" data-id="element-1046">{group.description}</p>
                <div className="flex items-start gap-2 text-sm text-gray-500" data-id="element-1047">
                  <Clock className="w-4 h-4 mt-0.5 shrink-0" data-id="element-1048" />
                  <p data-id="element-1049">{group.schedule}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-black" data-id="element-1050">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1051">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1052">
            <div data-id="element-1053">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1054">
                Why Choose Taekwondo?
              </h2>
              <div className="space-y-4" data-id="element-1055">
                {['Build strength, flexibility, and cardiovascular fitness', 'Develop discipline, focus, and self-control', 'Learn effective self-defense techniques', 'Gain confidence and self-esteem', 'Progress through belt ranks and achieve goals', 'Join a supportive community of martial artists'].map(benefit => <div key={benefit} className="flex items-start gap-3" data-id="element-1056">
                    <Check className="w-5 h-5 text-[#00D4FF] shrink-0 mt-1" data-id="element-1057" />
                    <p className="text-gray-300" data-id="element-1058">{benefit}</p>
                  </div>)}
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden" data-id="element-1059">
              <img src="https://cdn.magicpatterns.com/uploads/72kbAyD8TZrqRM2LNwiomw/Joe_Gabriel_Caitlin.png" alt="Taekwondo Training" className="w-full h-full object-cover object-top" data-id="element-1060" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-1061" />
            </div>
          </div>
        </div>
      </section>

      <CTASection data-id="element-1062" />
      <Footer data-id="element-1063" />
    </div>;
}